import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  AccessPermission,
  AccessRole,
  Department,
  Organization,
  fetchAccessRequests,
  fetchDepartments,
  fetchOrganizations,
  fetchRoles,
} from '../../api/accessApi';
import AuditTab from './access/AuditTab';
import DepartmentsTab from './access/DepartmentsTab';
import OrganizationsTab from './access/OrganizationsTab';
import RequestsTab from './access/RequestsTab';
import RolesTab from './access/RolesTab';
import UsageTab from './access/UsageTab';
import UsersTab from './access/UsersTab';

type TabId = 'requests' | 'users' | 'roles' | 'orgs' | 'departments' | 'usage' | 'audit';

interface AccessAdminProps {
  permissions: string[];
}

export default function AccessAdmin({ permissions }: AccessAdminProps) {
  const has = (p: string) => permissions.includes(p);
  const { user } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const tabs = ([
    has('manage-users') && ['requests', 'Access requests'],
    has('manage-users') && ['users', 'Users'],
    (has('manage-users') || has('manage-roles')) && ['roles', 'Roles & permissions'],
    has('manage-organizations') && ['orgs', 'Organizations'],
    has('manage-users') && ['departments', 'Departments'],
    has('manage-users') && ['usage', 'Tool usage'],
    has('view-audit-log') && ['audit', 'Audit log'],
  ].filter(Boolean) as [TabId, string][]);

  const [tab, setTab] = useState<TabId | null>(tabs[0]?.[0] ?? null);
  const [roles, setRoles] = useState<AccessRole[]>([]);
  const [allPermissions, setAllPermissions] = useState<AccessPermission[]>([]);
  const [grantable, setGrantable] = useState<string[]>([]);
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loadError, setLoadError] = useState('');

  const needsLookups = has('manage-users') || has('manage-roles');
  const loadRoles = useCallback(() => {
    if (!needsLookups) return;
    fetchRoles()
      .then((d) => {
        setRoles(d.roles);
        setAllPermissions(d.permissions);
        setGrantable(d.grantable);
      })
      .catch((e) => setLoadError(e.message));
  }, [needsLookups]);
  const loadPending = useCallback(() => {
    if (!has('manage-users')) return;
    fetchAccessRequests('pending')
      .then((d) => setPendingCount(d.pending_count))
      .catch(() => undefined); // the tab itself reports errors
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions]);
  const loadOrgs = useCallback(() => {
    if (!has('manage-organizations') && !has('manage-users')) return;
    fetchOrganizations()
      .then(setOrgs)
      .catch((e) => setLoadError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions]);

  const loadDepartments = useCallback(() => {
    if (!has('manage-users')) return;
    fetchDepartments()
      .then(setDepartments)
      .catch((e) => setLoadError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions]);

  useEffect(() => {
    loadRoles();
    loadDepartments();
    loadOrgs();
    loadPending();
  }, [loadRoles, loadOrgs, loadPending, loadDepartments]);

  const myLevel = roles.find((r) => r.name === user?.role)?.level ?? 0;

  if (!tab) {
    return <div className="empty">Your account does not have access to Access Management.</div>;
  }

  return (
    <>
      <div className="head">
        <div>
          <h1>Access Management</h1>
          <p className="lede">Manage who can sign in, what they can do, and review account activity.</p>
        </div>
      </div>
      <div className="filters">
        {tabs.map(([id, label]) => (
          <button key={id} className="chip" aria-pressed={tab === id} onClick={() => setTab(id)}>
            {label}
            {id === 'requests' && pendingCount > 0 && <span className="acc-badge">{pendingCount}</span>}
          </button>
        ))}
      </div>
      {loadError && <p className="acc-error">{loadError}</p>}
      <div className="acc-body">
        {tab === 'requests' && (
          <RequestsTab roles={roles} orgs={orgs} myLevel={myLevel} onChanged={loadPending} />
        )}
        {tab === 'users' && (
          <UsersTab
            roles={roles}
            orgs={orgs}
            departments={departments}
            myLevel={myLevel}
          />
        )}
        {tab === 'roles' && (
          <RolesTab roles={roles} permissions={allPermissions} grantable={grantable} onSaved={loadRoles} />
        )}
        {tab === 'orgs' && <OrganizationsTab orgs={orgs} onChanged={loadOrgs} />}
        {tab === 'departments' && (
          <DepartmentsTab
            departments={departments}
            permissions={allPermissions}
            grantable={grantable}
            onChanged={loadDepartments}
          />
        )}
        {tab === 'usage' && <UsageTab />}
        {tab === 'audit' && <AuditTab />}
      </div>
    </>
  );
}
